import {inject, Injectable} from '@angular/core';
import {BehaviorSubject, EMPTY, from, Observable, of, throwError} from 'rxjs';
import {catchError, filter, finalize, map, switchMap, take, tap} from 'rxjs/operators';
import {NotificationsService} from '../../../@core/notifications.service';
import {PatientDetail, PatientService} from '../../../@core/patient.service';
import {ConfirmService} from '../confirm/confirm.service';
import {ToastService} from '../../../@core/toast.service';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {
    APIService,
    SmartInsuranceVerificationDetailSerializer,
    SmartInsuranceVerificationSaveSerializer,
    SmartInsuranceVerificationViewSetQueryParams,
} from '../../../@core/api.service';
import {
    SmartInsuranceEligibilityConfirmModalComponent,
} from './smart-insurance-eligibility-confirm-modal/smart-insurance-eligibility-confirm-modal.component';
import {Insurance} from '../../../models/insurance';
import {SmartInsuranceConfirmModalResult, SmartInsuranceResultModal} from './smart-insurance-eligibility.types';
import {SmartInsuranceVerification} from './smartinsurance-verification';

interface PatientCheckState {
    patientId: number;
    smartInsuranceId: number | null;
    loading: boolean;
    result: SmartInsuranceVerification | null;
    error: string | null;
}

@Injectable({
    providedIn: 'root',
})
export class SmartInsuranceEligibilityService {
    private _notificationService = inject(NotificationsService);
    private _confirmService = inject(ConfirmService);
    private _modalService = inject(NgbModal);
    private toastService = inject(ToastService);
    private _patientChecks = new Map<number, BehaviorSubject<PatientCheckState>>();

    constructor() {
        this._notificationService.broadcast.subscribe(notification => {
            if (notification.notification_type === 'smart_insurance_result') {
                const smartInsuranceId = notification.target_object_id;

                if (smartInsuranceId && notification.patient) {
                    this._handleNotificationForPatient(notification.patient, smartInsuranceId);
                }
            }
        });
    }

    listVerifications(params: SmartInsuranceVerificationViewSetQueryParams): Observable<SmartInsuranceVerification[]> {
        return APIService.SmartInsuranceVerificationViewSet.list(params).pipe(
            map(results => results.map(r => this._mapVerification(r)))
        );
    }

    getLastVerification(patientId: number): Observable<SmartInsuranceVerification | null> {
        return this.listVerifications({
            patient: patientId,
            ordering: '-timestamp',
        }).pipe(
            map(results => results?.[0] || null),
        );
    }

    retrieveVerification(id: number): Observable<SmartInsuranceVerification> {
        return APIService.SmartInsuranceVerificationViewSet.retrieve(id).pipe(
            map(r => this._mapVerification(r))
        );
    }

    private _createVerification(data: SmartInsuranceVerificationSaveSerializer,
                                stateSubject: BehaviorSubject<PatientCheckState>,
                                currentState: PatientCheckState): Observable<SmartInsuranceVerification> {
        return APIService.SmartInsuranceVerificationViewSet.create(data).pipe(
            tap(() => {
                stateSubject.next({
                    ...currentState,
                    loading: true,
                    error: null,
                });
                this.toastService.success('Eligibility check initiated');
            }),
            map(r => this._mapVerification(r)),
            catchError(error => {
                this.toastService.error('Failed to initiate eligibility check');
                return throwError(() => error);
            })
        );
    }

    acceptVerification(id: number): Observable<void> {
        return APIService.SmartInsuranceVerificationViewSet.accept(id, {}).pipe(
            map(() => void 0)
        );
    }

    private _mapVerification(data: SmartInsuranceVerificationDetailSerializer): SmartInsuranceVerification {
        return new SmartInsuranceVerification(data);
    }

    getLoadingState(patientId: number): Observable<boolean> {
        return this._getOrCreatePatientState(patientId).pipe(
            map(state => state.loading)
        );
    }

    getResult(patientId: number): Observable<SmartInsuranceVerification | null> {
        return this._getOrCreatePatientState(patientId).pipe(
            map(state => state.result)
        );
    }

    getError(patientId: number): Observable<string | null> {
        return this._getOrCreatePatientState(patientId).pipe(
            map(state => state.error)
        );
    }

    isCheckInProgress(patientId: number): Observable<boolean> {
        if (!this._patientChecks.size) {
            return this.listVerifications({
                patient: patientId,
                status__in: ['QUEUED', 'IN_PROGRESS'],
            }).pipe(
                map(res => {
                    if (!res.length) return false;
                    const stateSubject = this._getOrCreatePatientStateSubject(patientId);
                    stateSubject.next({
                        patientId,
                        smartInsuranceId: res[0].id,
                        loading: res[0].status === 'QUEUED' || res[0].status === 'IN_PROGRESS',
                        result: res[0],
                        error: res[0].error_message || null,
                    });
                    return stateSubject.value.loading;
                }),
                switchMap(x => of(x)),
            );
        }
        const state = this._patientChecks.get(patientId);
        return of(!!state && state.value.loading);
    }

    runEligibilityCheck(patientDetail: PatientDetail, silentMode = false): void {
        this.isCheckInProgress(patientDetail.id).pipe(take(1)).subscribe(inProgress => {
            if (inProgress) {
                this.toastService.warning('An eligibility check is already in progress for this patient');
                return;
            }

            const stateSubject = this._getOrCreatePatientStateSubject(patientDetail.id);
            const currentState = stateSubject.value;

            if (silentMode) {
                this._createVerification({
                    patient: patientDetail.id,
                    update_insurance_settings: true,
                }, stateSubject, currentState).pipe(take(1)).subscribe();
                return;
            }

            patientDetail.insuranceSettings$.pipe(take(1)).subscribe(insuranceSettings => {
                if (!this._hasInsuranceCoverage(insuranceSettings)) {
                    this._handleNoCoverage(patientDetail.id, stateSubject, currentState);
                    return;
                }
                this._handleWithCoverage(patientDetail.id, stateSubject, currentState);
            });
        });
    }

    private _hasInsuranceCoverage(insuranceSettings: Insurance): boolean {
        return !!insuranceSettings && !insuranceSettings.empty && !!insuranceSettings.insurance_coverages?.length;
    }

    private _handleNoCoverage(patientId: number, stateSubject: BehaviorSubject<PatientCheckState>, currentState: PatientCheckState): void {
        this._confirmService.requestConfirm({
            title: 'Run Smart Insurance Discovery',
            body: 'No insurance coverage found for this patient. Do you want to proceed with the Smart Insurance Discovery?',
            showCancelBtn: true,
        }).pipe(
            switchMap(() => this._createVerification({
                patient: patientId,
                update_insurance_settings: true,
            }, stateSubject, currentState)),
        ).subscribe();
    }

    private _handleWithCoverage(patientId: number, stateSubject: BehaviorSubject<PatientCheckState>, currentState: PatientCheckState): void {
        const modal = this._modalService.open(SmartInsuranceEligibilityConfirmModalComponent);

        modal.componentInstance.modalDismiss = (event: Event, result: SmartInsuranceConfirmModalResult) => {
            event.stopPropagation();
            modal.dismiss(result);

            if (result === 'proceed' || result === 'userNeedsToConfirm') {
                this._createVerification({
                    patient: patientId,
                    update_insurance_settings: result !== 'userNeedsToConfirm',
                },
                stateSubject,
                currentState).subscribe();
            }
        };
    }

    private _getOrCreatePatientStateSubject(patientId: number): BehaviorSubject<PatientCheckState> {
        if (!this._patientChecks.has(patientId)) {
            const initialState: PatientCheckState = {
                patientId,
                smartInsuranceId: null,
                loading: false,
                result: null,
                error: null,
            };
            this._patientChecks.set(patientId, new BehaviorSubject(initialState));
        }
        return this._patientChecks.get(patientId)!;
    }

    private _getOrCreatePatientState(patientId: number): Observable<PatientCheckState> {
        return this._getOrCreatePatientStateSubject(patientId).asObservable();
    }

    private _openResultModal(data: SmartInsuranceVerification): Observable<SmartInsuranceResultModal> {
        return from(import('./smart-insurance-eligibility-result-modal/smart-insurance-eligibility-result-modal.component')).pipe(
            switchMap(({SmartInsuranceEligibilityResultModalComponent}) => new Observable<SmartInsuranceResultModal>(observer => {
                const modal = this._modalService.open(SmartInsuranceEligibilityResultModalComponent);
                modal.componentInstance.data = data;

                modal.componentInstance.modalDismiss = (result: SmartInsuranceResultModal) => {
                    modal.dismiss(result);
                };

                // Handle modal being closed by clicking outside or pressing ESC
                modal.dismissed.subscribe(x => {
                    observer.next(x);
                    observer.complete();
                });
            }))
        );
    }

    private _handleManualConfirmation(
        patientId: number,
        result: SmartInsuranceVerification,
        stateSubject: BehaviorSubject<PatientCheckState>
    ) {
        return this._openResultModal(result).pipe(
            tap(modalResult => {
                if (modalResult !== 'accept') {
                    stateSubject.next({
                        ...stateSubject.value,
                        loading: false,
                    });
                }
            }),
            filter(modalResult => modalResult === 'accept'),
            switchMap(() => this.acceptVerification(result.id).pipe(
                tap(() => {
                    this.toastService.success('Insurance settings updated with eligibility check result');
                    PatientService.invalidate(patientId, 'insuranceSettings');
                    PatientService.invalidate(patientId, 'address');
                })
            ))
        );
    }

    private _handleNotificationForPatient(patientId: number, smartInsuranceId: number): void {
        const stateSubject = this._patientChecks.get(patientId);

        if (!stateSubject) {
            this._getOrCreatePatientState(patientId).pipe(take(1)).subscribe(() => {
                this._handleNotificationForPatient(patientId, smartInsuranceId);
            });
            return;
        }

        this.retrieveVerification(smartInsuranceId).pipe(
            // Update state with result
            tap(result => {
                stateSubject.next({
                    ...stateSubject.value,
                    loading: false,
                    result,
                    error: null,
                });
            }),
            // Branch based on update_insurance_settings flag
            switchMap(result => {
                if (result.update_insurance_settings) {
                    // Auto update - no user interaction needed
                    PatientService.invalidate(patientId, 'insuranceSettings');
                    return EMPTY; // Complete immediately
                }
                return this._handleManualConfirmation(patientId, result, stateSubject);
            }),
            // Cleanup - always runs
            finalize(() => this.resetPatient(patientId)),
            catchError(error => {
                this.toastService.error('Failed to process Smart Insurance Discovery result');
                console.error('Smart Insurance Discovery:', error);
                return EMPTY;
            })
        ).subscribe();
    }

    resetPatient(patientId: number): void {
        const stateSubject = this._patientChecks.get(patientId);
        if (stateSubject) this._patientChecks.delete(patientId);
    }

    resetAll(): void {
        this._patientChecks.forEach((stateSubject, patientId) => {
            this.resetPatient(patientId);
        });
    }

    handleManualConfirmation(
        patientId: number,
        result: SmartInsuranceVerification
    ): Observable<void> {
        const stateSubject = this._getOrCreatePatientStateSubject(patientId);
        return this._handleManualConfirmation(patientId, result, stateSubject);
    }
}
